"use client";

import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useProjectText } from "@/features/project/useProjectText";
import { cn } from "@/lib/cn";
import type { FilterWithFacet } from "../useFacets";
import { useSelectedFilters } from "../useSelectedFilters";
import { DateFilter } from "./DateFilter";
import { RangeFilter } from "./RangeFilter";
import { ValueFilter } from "./ValueFilter";

/**
 * One filter: a pill that opens its control.
 *
 * The pill carries the tenant's icon, its label, and how many values are
 * selected — one for a range, since a range is a single choice however many
 * numbers describe it.
 */
export function Filter({
  filter,
  isOpen,
  setOpen,
  isCompact,
}: {
  filter: FilterWithFacet;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  /** The search input is expanded, so the pills give up their labels for it. */
  isCompact: boolean;
}) {
  const t = useProjectText();
  const configs = useProjectConfigs();
  const { selectedFilters } = useSelectedFilters();

  const { def } = filter;
  const selected = selectedFilters[def.name] ?? [];
  const label = t(def.name, def.name);
  const selectedCount =
    def.kind === "simple" ? selected.length : selected.length > 0 ? 1 : 0;

  return (
    <div className="relative inline-block min-w-[43px] text-left">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        aria-expanded={isOpen}
        className={cn(
          "group flex h-8 w-full transform items-center justify-between space-x-2 rounded-full px-2 py-2",
          "tablet:text-xs font-medium transition-transform duration-300 tablet:custom-hover",
          "hover:dynamic-text focus:outline-none tablet:focus:ring-2 focus:ring-blue-500",
          isOpen
            ? "tablet:bg-primary tablet:ring-2 ring-primary dynamic-text"
            : "bg-white tablet:ring-1 ring-gray-300 dark:bg-gray-800 dark:text-off-white dark:ring-gray-600"
        )}
      >
        <i
          className={`icon ic-${
            def.icon || "mpr_settings"
          } shadow-sm p-2 border rounded-full tablet:shadow-none tablet:border-none`}
          aria-hidden
        />
        {!isCompact && <span className="flex-grow text-left">{label}</span>}
        {!isCompact && selectedCount > 0 && (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              isOpen
                ? "bg-white text-black dark:bg-dark-color dark:text-off-white"
                : "bg-primary dynamic-text group-hover:bg-white group-hover:text-black dark:group-hover:bg-dark-color dark:group-hover:text-white"
            )}
          >
            {selectedCount}
          </span>
        )}
        <i
          className={cn(
            "icon text-base group-hover:dynamic-text border shadow rounded-lg px-1.5 py-1 tablet:border-none tablet:shadow-none tablet:p-0",
            isOpen
              ? "ic-material_ic_keyboard_arrow_up_24px dynamic-text"
              : "ic-material_ic_keyboard_arrow_down_24px text-black dark:text-white"
          )}
          aria-hidden
        />
      </button>

      <div
        className={cn(
          "tablet:fixed tablet:z-50 mt-3 rounded-lg bg-white tablet:shadow-lg tablet:ring-1 ring-black ring-opacity-5",
          "transition-all duration-300 ease-out dark:bg-dark-color overflow-hidden",
          def.kind !== "daterange" && "tablet:w-56",
          isOpen
            ? "visible opacity-100 max-h-[999px]"
            : "max-h-0 tablet:invisible opacity-0"
        )}
      >
        {def.kind === "daterange" ? (
          <DateFilter filter={filter} label={label} />
        ) : def.kind === "range" ? (
          <RangeFilter filter={filter} title={label} />
        ) : (
          <ValueFilter
            filter={filter}
            label={label}
            // Under `or` the user is expected to pick several, so closing after
            // each tick would make choosing three values three trips through the
            // pill. Under `and` each choice narrows, so one at a time is the
            // point.
            onToggled={() => {
              if (configs.filterLogic !== "or") setOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
